import { convertToArabicNumbers } from '@/utils/helpers/numberHelpers';
import type { Bilingual } from '@/types/orderUpdates';

// Core sends Baghdad wall time with its offset ("2026-09-13T22:26:36+03:00").
// We render the wall time AS SENT — never through Date/dayjs, which would
// re-zone it to the viewer's machine (formatDateByDayjs adds 3 h on top).
const ISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;
const EN_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const AR_MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

const isAr = (lang: string) => lang.startsWith('ar');
const digits = (s: string, lang: string) => (isAr(lang) ? convertToArabicNumbers(s) : s);

const parts = (at: string | null | undefined) => {
  const m = ISO.exec(at ?? '');
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const month = Number(mo) - 1;
  if (month < 0 || month > 11) return null;
  return { y, month, d: String(Number(d)), h: Number(h), mi };
};

export const formatUpdateDay = (at: string | null | undefined, lang: string): string => {
  const p = parts(at);
  if (!p) return '';
  const month = isAr(lang) ? AR_MONTHS[p.month] : EN_MONTHS[p.month];
  return digits(`${p.d} ${month} ${p.y}`, lang);
};

export const formatUpdateClock = (at: string | null | undefined, lang: string): string => {
  const p = parts(at);
  if (!p) return '';
  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  const suffix = isAr(lang) ? (p.h < 12 ? 'ص' : 'م') : p.h < 12 ? 'AM' : 'PM';
  return digits(`${h12}:${p.mi} ${suffix}`, lang);
};

/** "28 Dec" / "٢٨ ديسمبر" — the year is left to the caller (timeline dividers, summary). */
export const formatUpdateDayShort = (at: string | null | undefined, lang: string): string => {
  const p = parts(at);
  if (!p) return '';
  return digits(`${p.d} ${isAr(lang) ? AR_MONTHS[p.month] : EN_MONTHS[p.month]}`, lang);
};

/** "22 Sep, 10:43 AM" / "٢٢ سبتمبر، ١٠:٤٣ ص". */
export const formatUpdateShort = (at: string | null | undefined, lang: string): string => {
  const day = formatUpdateDayShort(at, lang);
  return day ? `${day}${isAr(lang) ? '، ' : ', '}${formatUpdateClock(at, lang)}` : '';
};

/** "28 Dec 2025 · 10:15 AM" / "٢٨ ديسمبر ٢٠٢٥ · ١٠:١٥ ص" — empty for an unparseable value. */
export const formatUpdateTime = (at: string | null | undefined, lang: string): string => {
  const day = formatUpdateDay(at, lang);
  return day ? `${day} · ${formatUpdateClock(at, lang)}` : '';
};

/** Calendar day of the wall time, for grouping ("2026-09-13"). */
export const updateDayKey = (at: string | null | undefined): string => (at ?? '').slice(0, 10);

/** 70 → "1:10". */
export const formatCallDuration = (seconds: number, lang: string): string =>
  digits(`${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`, lang);

/** The API sends bilingual copy; show the app's language, fall back to the other. */
export const pickLang = (value: Bilingual | null | undefined, lang: string): string =>
  (isAr(lang) ? value?.ar || value?.en : value?.en || value?.ar) || '';

export const localDigits = digits;
